import { useState, useMemo, useEffect } from 'react'
import { twoProportionZTest, calculateConfidenceIntervals } from '@/lib/utils/abTestStatistics'
import {
  ToolPage,
  ToolCard,
  ToolFieldRow,
  ToolNumberField,
  ToolSegmented,
  ToolResultCard,
  ToolStatRows,
  ToolVerdict,
} from '@/components/tools/ToolKit'

const CONFIDENCE_LEVELS = [
  { label: '90%', value: 90 },
  { label: '95%', value: 95 },
  { label: '97%', value: 97 },
  { label: '99%', value: 99 },
]

const pct = (n: number, digits = 2) => `${(n * 100).toFixed(digits)}%`
const signed = (n: number, digits = 1) => `${n > 0 ? '+' : ''}${n.toFixed(digits)}%`

type Inputs = { controlVisitors: string; controlConversions: string; variantVisitors: string; variantConversions: string }

/** `initial` only seeds the dev states gallery; the route renders it empty. */
export default function SignificanceCalculator({ initial }: { initial?: Inputs } = {}) {
  const [controlVisitors, setControlVisitors] = useState(initial?.controlVisitors ?? '')
  const [controlConversions, setControlConversions] = useState(initial?.controlConversions ?? '')
  const [variantVisitors, setVariantVisitors] = useState(initial?.variantVisitors ?? '')
  const [variantConversions, setVariantConversions] = useState(initial?.variantConversions ?? '')
  const [confidenceLevel, setConfidenceLevel] = useState(95)

  useEffect(() => {
    document.title = 'Significance Calculator - Humblytics'
  }, [])

  const results = useMemo(() => {
    const cV = parseInt(controlVisitors)
    const cC = parseInt(controlConversions)
    const vV = parseInt(variantVisitors)
    const vC = parseInt(variantConversions)

    if (!cV || !vV || cV <= 0 || vV <= 0) return null
    if (isNaN(cC) || isNaN(vC) || cC < 0 || vC < 0) return null
    if (cC > cV || vC > vV) return null

    const zTest = twoProportionZTest(cC, cV, vC, vV, confidenceLevel)
    const ci = calculateConfidenceIntervals(cC, cV, vC, vV, confidenceLevel / 100)

    const controlRate = cC / cV
    const variantRate = vC / vV
    const relativeLift = controlRate > 0 ? ((variantRate - controlRate) / controlRate) * 100 : 0

    const verdict: 'significant' | 'not_yet' | 'insufficient' =
      cV < 30 || vV < 30 ? 'insufficient' : zTest.isSignificant ? 'significant' : 'not_yet'

    return {
      controlRate,
      variantRate,
      relativeLift,
      zScore: zTest.zScore,
      pValue: zTest.pValue,
      confidenceReached: zTest.significancePercent,
      verdict,
      liftCI: ci.liftCI,
    }
  }, [controlVisitors, controlConversions, variantVisitors, variantConversions, confidenceLevel])

  const caption = (() => {
    if (!results) return undefined
    const lift = Math.abs(results.relativeLift).toFixed(1)
    const direction = results.relativeLift >= 0 ? 'better' : 'worse'
    if (results.verdict === 'insufficient') return 'Each group needs at least 30 visitors before the math means anything.'
    if (results.verdict === 'not_yet') return `Variant is ${lift}% ${direction} so far, but it could still be noise. Keep the test running.`
    return `Variant converts ${lift}% ${direction} than control, at ${confidenceLevel}% confidence.`
  })()

  const inputs = (
    <>
      <ToolCard title="Control">
        <ToolFieldRow>
          <ToolNumberField id="control-visitors" label="Visitors" placeholder="e.g. 4812" value={controlVisitors} onChange={setControlVisitors} />
          <ToolNumberField id="control-conversions" label="Conversions" placeholder="e.g. 168" value={controlConversions} onChange={setControlConversions} />
        </ToolFieldRow>
      </ToolCard>
      <ToolCard title="Variant">
        <ToolFieldRow>
          <ToolNumberField id="variant-visitors" label="Visitors" placeholder="e.g. 4790" value={variantVisitors} onChange={setVariantVisitors} />
          <ToolNumberField id="variant-conversions" label="Conversions" placeholder="e.g. 221" value={variantConversions} onChange={setVariantConversions} />
        </ToolFieldRow>
      </ToolCard>
      <ToolCard
        title="Confidence level"
        description="95% is the usual bar for calling a winner."
        action={
          <ToolSegmented label="Confidence level" options={CONFIDENCE_LEVELS} value={confidenceLevel} onChange={setConfidenceLevel} />
        }
      />
    </>
  )

  const aside = (
    <ToolResultCard
      title="Result"
      badge={
        results && (
          <ToolVerdict tone={results.verdict === 'significant' ? 'answer' : results.verdict === 'not_yet' ? 'caution' : 'neutral'}>
            {results.verdict === 'significant' ? 'Significant' : results.verdict === 'not_yet' ? 'Not yet significant' : 'Too little data'}
          </ToolVerdict>
        )
      }
      value={results ? signed(results.relativeLift) : undefined}
      answer={results?.verdict === 'significant'}
      caption={caption}
      empty="Enter visitors and conversions for both groups to see whether the difference is real."
    >
      {results && (
        <ToolStatRows
          rows={[
            { label: 'Control rate', value: pct(results.controlRate) },
            { label: 'Variant rate', value: pct(results.variantRate) },
            { label: 'Confidence reached', value: `${results.confidenceReached.toFixed(1)}%` },
            { label: 'Likely lift range', value: `${signed(results.liftCI.lower * 100)} to ${signed(results.liftCI.upper * 100)}` },
            { label: 'p-value', value: results.pValue.toFixed(4), mono: true },
            { label: 'z-score', value: results.zScore.toFixed(2), mono: true },
          ]}
        />
      )}
    </ToolResultCard>
  )

  return (
    <ToolPage
      title="Significance calculator"
      description="Check whether an A/B test result is a real difference or noise."
      inputs={inputs}
      aside={aside}
    />
  )
}
